/**
 * Wave 3 · B7 — tab-identity rule.
 *
 * A tab's identity is derived from its LOCATION, not created fresh on every
 * navigation: two visits to the same record land on the same tab; a
 * different record opens a different tab.
 *
 *   - Route params baked into the PATH (react-router `:param` segments —
 *     /products/:asin, /inventory/products/:uid, /rules/:id, ...) already
 *     differentiate the tab for free, because they're part of `pathname`.
 *   - A SMALL allowlist of query params also carry record identity on hub
 *     pages that deep-link into a record via `?param=` instead of the path
 *     (e.g. the Marketplaces hub's `?uid=` product deep link). Those are
 *     folded into the key.
 *   - Everything else in the query string — sort, filters, `?tab=` sub-tab
 *     selection, date ranges — is identity-NEUTRAL: changing it updates the
 *     open tab's remembered location but does NOT spawn a new tab.
 *
 * This is deliberately a conservative allowlist (safe subset) rather than an
 * attempt to enumerate every deep-link param in the app — see PR_BODY.md
 * "known limitations" for pages that use a query param for record identity
 * that isn't in this list yet (they'll just share one tab per page instead
 * of one per record, which is the pre-tabs behavior anyway, not a regression).
 */
import { DEFAULT_TAB, resolveSettingsUrl, tabLabel } from '../pages/settings/routing'
import { adsTabTitle } from '../pages/ads/routing'

const IDENTITY_QUERY_PARAMS = ['uid']

/**
 * Master-detail pages whose `/:param` tail picks an item INSIDE one page (a
 * list on the left, the picked item on the right) rather than opening a
 * separate record. Clicking down the list must not spawn a tab per click — the
 * 8-tab cap would start evicting the user's other tabs within a few clicks —
 * so every path under one of these shares the page's single tab, whose
 * remembered location follows the latest pick. A deep link still lands on the
 * item (the path is kept; only the tab IDENTITY ignores the tail).
 */
const SINGLE_TAB_PAGES = ['/inventory/product-settings']

export function deriveTabKey(pathname: string, search: string): string {
  const params = new URLSearchParams(search)
  const page = SINGLE_TAB_PAGES.find((p) => pathname === p || pathname.startsWith(`${p}/`))
  if (page) return page
  const parts: string[] = [pathname]
  for (const key of IDENTITY_QUERY_PARAMS) {
    const v = params.get(key)
    if (v) parts.push(`${key}=${v}`)
  }
  return parts.join('?')
}

interface TitleRule {
  re: RegExp
  title: (m: RegExpMatchArray, params: URLSearchParams) => string
}

// Ordered — first match wins. Kept purely mechanical (route → label); pages
// that want a richer title (an actual product name instead of "Product
// <uid>") can upgrade later via a small "register my title" hook — out of
// scope for this pass, see PR_BODY.md.
const TITLE_RULES: TitleRule[] = [
  // P3 (Daily Brief, 2026-07-21): /overview's sidebar label + default content
  // are now "Daily Brief" (?tab=dashboard still reaches the old Command
  // Center content) — the record-tab title follows. `tab` isn't in
  // IDENTITY_QUERY_PARAMS above (by design — sub-tab selection is identity-
  // neutral), so switching sub-tabs relabels this SAME tab rather than
  // spawning a new one.
  { re: /^\/overview\/?$/, title: () => 'Daily Brief' },
  // PR 4 (A6): the Advertising hub is ONE tab however deep you drill (no
  // identity param), titled by what it is scoped to: "Advertising · Blue".
  // reconcile.ts refreshes an open tab's title as the scope changes.
  { re: /^\/ads\/?$/, title: (_m, p) => adsTabTitle(`?${p.toString()}`) },
  // A7 (PR 22d): the record-tab title is the sidebar label (and the page's
  // H1), pinned for every sidebar entry by sidebarTabTitles.test.ts.
  { re: /^\/campaigns\/new\/?$/, title: () => 'New campaign' },
  { re: /^\/reports(\/ads-overview)?\/?$/, title: () => 'Ads overview' },
  { re: /^\/audit\/?$/, title: () => 'Account audit' },
  { re: /^\/history\/?$/, title: () => 'Change history' },
  { re: /^\/deals\/?$/, title: () => 'Deals' },
  { re: /^\/alerts\/?$/, title: () => 'Alerts' },
  { re: /^\/campaigns\/?$/, title: () => 'Campaigns' },
  { re: /^\/ad-groups\/?$/, title: () => 'Ad Groups' },
  { re: /^\/targets\/?$/, title: () => 'Targets' },
  { re: /^\/search-terms\/?$/, title: () => 'Search Terms' },
  { re: /^\/placements\/?$/, title: () => 'Placements' },
  { re: /^\/suggestions\/?$/, title: () => 'Suggestions' },
  { re: /^\/products\/([^/]+)\/?$/, title: (m) => `Product ${decodeURIComponent(m[1])}` },
  { re: /^\/products\/?$/, title: () => 'Products' },
  { re: /^\/hourly\/?$/, title: () => 'Hourly' },
  { re: /^\/strategies\/?$/, title: () => 'Strategies' },
  { re: /^\/listing\/?$/, title: () => 'Listing' },
  { re: /^\/audiences\/([^/]+)\/?$/, title: (m) => `Audience ${decodeURIComponent(m[1])}` },
  { re: /^\/audiences\/?$/, title: () => 'Audiences' },
  { re: /^\/agents\/?$/, title: () => 'Agents' },
  { re: /^\/ai-assistant\/?$/, title: () => 'AI Assistant history' },
  // Automations port (CONTRACTS 10.2). The specific patterns sit above the
  // catch-all `^/rules/([^/]+)/?$`, which would otherwise title the classic
  // hub "Rule legacy". `?tab=` is identity-neutral, so the hub's four tabs
  // (and the sidebar's Activity / Custom metrics / Saved filters entries) share
  // one "Automations" tab.
  { re: /^\/rules\/legacy\/([^/]+)\/edit\/?$/, title: (m) => `Edit rule ${decodeURIComponent(m[1])} (classic)` },
  { re: /^\/rules\/legacy(\/.*)?$/, title: () => 'Rules (classic)' },
  // A new filter and each saved one are told apart, as automations are
  // ("New automation" / "Edit automation 42").
  { re: /^\/rules\/saved-filters\/new\/?$/, title: () => 'New saved filter' },
  { re: /^\/rules\/saved-filters\/([^/]+)\/?$/, title: (m) => `Saved filter ${decodeURIComponent(m[1])}` },
  { re: /^\/rules\/saved-filters(\/.*)?$/, title: () => 'Saved filters' },
  { re: /^\/rules\/preflight\/?$/, title: () => 'Go-Live Preflight' },
  { re: /^\/rules\/new\/?$/, title: () => 'New automation' },
  { re: /^\/rules\/([^/]+)\/edit\/?$/, title: (m) => `Edit automation ${decodeURIComponent(m[1])}` },
  { re: /^\/rules\/([^/]+)\/?$/, title: (m) => `Rule ${decodeURIComponent(m[1])}` },
  { re: /^\/rules\/?$/, title: () => 'Automations' },
  { re: /^\/inventory\/products\/([^/]+)\/?$/, title: (m) => `Product ${decodeURIComponent(m[1])}` },
  { re: /^\/inventory\/marketplaces\/?$/, title: (_m, p) => (p.get('uid') ? `Product ${p.get('uid')}` : 'Marketplaces') },
  // One title whatever ?tab= says (the page H1 and the sidebar say the same;
  // the strip inside the page names the sub-tab).
  { re: /^\/inventory\/orders\/?$/, title: () => 'Orders & shipments' },
  // Feedback #118 — one tab for the whole page (SINGLE_TAB_PAGES above).
  { re: /^\/inventory\/product-settings(\/.*)?$/, title: () => 'Product settings' },
  { re: /^\/inventory\/document-receive\/?$/, title: () => 'Document Receive' },
  { re: /^\/inventory\/folder-map\/?$/, title: () => 'Folder Map' },
  // Settings (PR 7, A7): the record-tab title says what the page's H1 and
  // sidebar label say, plus the tab when it is not the default (Users).
  // Retired ?tab= values read as their new home (routing.ts).
  //
  // The title names what EVERY viewer is sure to see, so it never names a tab
  // the page refused to show. This rule cannot see who is looking, so it
  // resolves the URL as a non-admin would: an admin tab (System health, the
  // bell's System notifications, Advanced, ...) is `shown` as Users for a
  // non-admin, and the title is plain "Settings" — true for an admin on that
  // tab too, and never "Settings · System health" over the Users tab.
  { re: /^\/ops\/?$/, title: (_m, p) => {
    const { shown } = resolveSettingsUrl(p.toString(), false)
    return shown === DEFAULT_TAB ? 'Settings' : `Settings · ${tabLabel(shown)}`
  } },
  // The workflow library (Phase 12 Build 37).
  { re: /^\/workflows\/?$/, title: () => 'Workflows' },
  // Phase 12 Build 38. Its own path, NOT /workflows/... — the two pages are
  // different things (procedure for people vs drafted instruction for the
  // runner), so neither regex can shadow the other.
  { re: /^\/automation-drafts\/?$/, title: () => 'Automation Drafts' },
]

export function deriveTabTitle(pathname: string, search: string): string {
  const params = new URLSearchParams(search)
  for (const rule of TITLE_RULES) {
    const m = pathname.match(rule.re)
    if (m) return rule.title(m, params)
  }
  // Fallback: last non-empty path segment, title-cased.
  const segs = pathname.split('/').filter(Boolean)
  const last = segs[segs.length - 1] ?? 'ClickPay'
  return decodeURIComponent(last)
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}
